import { errorMessageFrom } from "../../../../../utils/formErrors";
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Stack, Dialog, TextField, CircularProgress, IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import EmptyState from "../../../../../component/EmptyState";
import SectionLoader from "../../../../../component/SectionLoader";
import { useNotify } from "../../../../../component/NotificationProvider";
import { useActiveLegalUnit } from "../../../../../services/useOrgData";
import { tokens, displayFont, radius } from "../../../../../theme";

const cardSx = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px` };

/**
 * Reusable entity-scoped "named item" manager (departments, designations).
 * Provide: fetch(), create(name), and optionally remove(item) services.
 */
export default function OrgNamedList({
  title, addLabel, singular,
  fetchItems, createItem, removeItem, // async services
  emptyText,
  rowExtra, // optional (item, reload) => node, rendered before the delete action
}) {
  const notify = useNotify();
  const active = useActiveLegalUnit();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const list = await fetchItems();
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || `Failed to load ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await createItem(name.trim());
      if (res?.success !== false) {
        notify.success(`${singular} added`);
        setName("");
        setDialogOpen(false);
        load();
      } else {
        notify.error(res?.message || `Could not add ${singular.toLowerCase()}`);
      }
    } catch (err) {
      notify.error(err?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      const res = await removeItem(item);
      if (res?.success !== false) {
        notify.success(`${singular} removed`);
        load();
      } else {
        notify.error(errorMessageFrom(res, `Could not remove ${singular.toLowerCase()}`));
      }
    } catch (err) {
      notify.error(err?.message || "Something went wrong");
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
        <Box>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{title}</Box>
          {active?.name && (
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>In {active.name}</Box>
          )}
        </Box>
        <BtnPrimary onClick={() => { setName(""); setDialogOpen(true); }} sx={{ borderRadius: "8px" }}>
          <AddIcon sx={{ fontSize: "1.1rem", mr: 0.5 }} /> {addLabel}
        </BtnPrimary>
      </Stack>

      {loading ? (
        <SectionLoader />
      ) : error ? (
        <EmptyState variant="error" title={`Couldn't load ${title.toLowerCase()}`} onRetry={load} />
      ) : items.length === 0 ? (
        <EmptyState title={`No ${title.toLowerCase()} yet`} description={emptyText} />
      ) : (
        <Stack spacing={1}>
          {items.map((item) => (
            <Stack key={item._id || item.name} direction="row" alignItems="center" justifyContent="space-between"
              sx={{ ...cardSx, p: "1rem 1.25rem" }}>
              <Box sx={{ fontSize: "0.9375rem", fontWeight: 500, color: tokens.text }}>{item.name}</Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                {rowExtra && rowExtra(item, load)}
                {removeItem && (
                  <IconButton aria-label="Delete" size="small" onClick={() => handleDelete(item)} sx={{ color: tokens.textMuted, "&:hover": { color: tokens.danger } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ sx: { borderRadius: "12px", width: "min(28rem,90vw)" } }}>
        <Box sx={{ p: "1.5rem" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{addLabel}</Box>
            <IconButton aria-label="Close" size="small" onClick={() => setDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>{singular} name *</Box>
          <TextField fullWidth size="small" autoFocus value={name} placeholder={`e.g. ${singular === "Department" ? "Engineering" : "Software Engineer"}`}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()} sx={{ mb: "1.5rem" }} />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <BtnSecondary onClick={() => setDialogOpen(false)} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleCreate} disabled={saving} sx={{ borderRadius: "8px" }}>
              {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Add"}
            </BtnPrimary>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
